import React, { useState, useEffect } from 'react'
import { Loader2 } from 'lucide-react'
import { TeamMember } from '@/types/dashboard'
import { findPersonInHierarchy } from '@/lib/real-data-service'

interface OrgChartNodeProps {
  member: TeamMember
  children?: TeamMember[]
  onMemberClick: (member: TeamMember) => void
}

interface OrgChartProps {
  member: TeamMember
  children?: TeamMember[]
  onMemberClick: (member: TeamMember) => void
}

// 🔄 HIERARCHY CONVERTER - Convert OrganizationalHierarchyNode to TeamMember with nested children
const convertHierarchyToTeamMember = (node: any, level: number = 0): TeamMember => {
  return {
    id: node.id,
    name: node.name,
    role: node.role,
    level: level,
    team: node.department,
    avatar: node.name.split(' ').map((n: string) => n[0]).join('').substring(0, 2),
    email: node.email,
    phone: '(11) 99999-0000',
    directReports: node.directReports?.length || 0,
    parentId: level === 0 ? undefined : node.parentId,
  };
};

// 📊 NESTED HIERARCHY CONVERTER - Convert children recursively
const convertHierarchyChildrenToTeamMembers = (node: any, level: number = 1): TeamMember[] => {
  if (!node.directReports || node.directReports.length === 0) {
    return [];
  }
  
  return node.directReports.map((child: any) => convertHierarchyToTeamMember(child, level));
};

export function OrgChartNode({ member, children, onMemberClick }: OrgChartNodeProps) {
  const [childrenData, setChildrenData] = useState<TeamMember[]>(children || [])
  const [isLoadingChildren, setIsLoadingChildren] = useState(false)
  const [expandedChildren, setExpandedChildren] = useState<{ [key: string]: TeamMember[] }>({})

  useEffect(() => {
    if (children && children.length > 0) {
      const loadChildrenHierarchy = async () => {
        try {
          setIsLoadingChildren(true)
          const childrenWithHierarchy: { [key: string]: TeamMember[] } = {}

          for (const child of children) {
            try {
              const childHierarchyNode = await findPersonInHierarchy(child.name)
              if (childHierarchyNode) {
                const childChildren = convertHierarchyChildrenToTeamMembers(childHierarchyNode, child.level + 1)
                childrenWithHierarchy[child.id] = childChildren
              } else {
                childrenWithHierarchy[child.id] = []
              }
            } catch (err) {
              console.error(`Error loading hierarchy for ${child.name}:`, err)
              childrenWithHierarchy[child.id] = []
            }
          }

          setExpandedChildren(childrenWithHierarchy)
        } catch (err) {
          console.error('Error loading children hierarchy:', err)
        } finally {
          setIsLoadingChildren(false)
        }
      }

      loadChildrenHierarchy()
    }
  }, [children])

  return (
    <div className="flex flex-col items-center animate-fade-in-up">
      <div 
        onClick={() => onMemberClick(member)}
        className="card-interactive group flex flex-col items-center p-6 mb-4 max-w-48"
      >
        <div className={`w-16 h-16 rounded-full flex items-center justify-center text-white font-bold text-lg mb-3 shadow-monumenta group-hover:shadow-monumenta-brand transition-all duration-200 ${
          member.level === 0 
            ? 'bg-gradient-to-br from-brand-primary to-brand-accent-600 text-brand-dark' 
            : 'bg-gradient-to-br from-brand-blue to-brand-accent-600'
        }`}>
          {member.avatar}
        </div>
        <h4 className="text-white font-medium font-display text-center mb-1 group-hover:text-brand-primary transition-colors duration-200 leading-tight">{member.name}</h4>
        <p className="text-sm text-brand-gray-light font-body text-center leading-tight">{member.role}</p>
        {member.directReports > 0 && (
          <div className="mt-3 px-3 py-1 bg-brand-primary/10 border border-brand-primary/20 rounded-monumenta text-xs text-brand-primary font-display font-medium animate-pulse">
            {member.directReports} pessoas
          </div>
        )}
      </div>
      
      {children && children.length > 0 && (
        <div className="relative">
          {/* Linha vertical */}
          <div className="absolute left-1/2 transform -translate-x-1/2 w-px h-6 bg-brand-primary/30 -top-2"></div>
          {/* Linha horizontal */}
          {children.length > 1 && (
            <div className="absolute top-4 left-0 right-0 h-px bg-brand-primary/30"></div>
          )}
          {/* Loading state for children */}
          {isLoadingChildren && (
            <div className="flex items-center justify-center pt-6 min-w-fit">
              <div className="text-center">
                <Loader2 className="w-4 h-4 text-brand-primary animate-spin mx-auto mb-2" />
                <p className="text-xs text-brand-gray-light font-body">Carregando hierarquia...</p>
              </div>
            </div>
          )}
          {/* Nós filhos */}
          {!isLoadingChildren && (
            <div className="flex gap-8 pt-6 min-w-fit">
              {children.map((child) => {
                // Get the child's hierarchy data from the loaded state
                const childChildren = expandedChildren[child.id] || []

                return (
                  <div key={child.id} className="relative flex-shrink-0">
                    {/* Linha vertical para cada filho */}
                    <div className="absolute left-1/2 transform -translate-x-1/2 w-px h-6 bg-brand-primary/30 -top-6"></div>
                    <OrgChartNode
                      member={child}
                      children={childChildren}
                      onMemberClick={onMemberClick}
                    />
                  </div>
                )
              })
            }
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export function OrgChart({ member, children, onMemberClick }: OrgChartProps) {
  return (
    <div className="overflow-x-auto py-4 w-full">
      <div className="flex justify-center min-w-fit">
        <OrgChartNode 
          member={member} 
          children={children}
          onMemberClick={onMemberClick}
        />
      </div>
    </div>
  );
}